import React, { useState, useEffect } from 'react';
import { X, Mail, Lock, AlertTriangle, Timer, CheckCircle, Key } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

// M-H — show the invite-code field on the signup form. Two paths:
//   • VITE_BETA_INVITE_REQUIRED=true → required, visible, dedicated field.
//   • unset / "false" → field is hidden; signups go through whatever
//     gating the server enforces (signup-with-invite passes everyone
//     through when BETA_INVITE_CODE on the server is also unset).
const INVITE_REQUIRED = (import.meta.env.VITE_BETA_INVITE_REQUIRED as string | undefined) === 'true';

interface AuthModalProps {
  isOpen: boolean;
  onClose: () => void;
  defaultMode?: 'signin' | 'signup';
  // When false (e.g. AuthGate), the X close button is hidden — there's no
  // app behind the modal to return to.
  dismissible?: boolean;
}

type Mode = 'signin' | 'signup' | 'reset' | 'updatePassword';

const AuthModal: React.FC<AuthModalProps> = ({ isOpen, onClose, defaultMode = 'signin', dismissible = true }) => {
  const [mode, setMode] = useState<Mode>(defaultMode);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [inviteCode, setInviteCode] = useState('');
  const [validationError, setValidationError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cooldownTime, setCooldownTime] = useState(0);
  const [resetSent, setResetSent] = useState(false);
  const [passwordUpdated, setPasswordUpdated] = useState(false);
  const {
    signIn, signUp, resetPassword, updatePassword,
    inPasswordRecovery,
    user, error, clearError,
  } = useAuth();

  useEffect(() => {
    let timer: ReturnType<typeof setInterval>;
    if (cooldownTime > 0) {
      timer = setInterval(() => setCooldownTime((p) => Math.max(0, p - 1)), 1000);
    }
    return () => { if (timer) clearInterval(timer); };
  }, [cooldownTime]);

  // Auto-close once the user becomes authenticated, EXCEPT when the auth
  // state is from a recovery flow — in that case we want the modal to stay
  // open in updatePassword mode.
  useEffect(() => {
    if (isOpen && user && !inPasswordRecovery && mode !== 'updatePassword') {
      setEmail('');
      setPassword('');
      setValidationError('');
      setResetSent(false);
      onClose();
    }
  }, [isOpen, user, inPasswordRecovery, mode, onClose]);

  // When supabase-js fires PASSWORD_RECOVERY (because the user arrived via
  // a reset email link), force the modal into "Set New Password" mode.
  useEffect(() => {
    if (inPasswordRecovery && mode !== 'updatePassword') {
      setMode('updatePassword');
      setPasswordUpdated(false);
    }
  }, [inPasswordRecovery, mode]);

  // Reset transient state whenever the modal opens or the mode changes.
  useEffect(() => {
    if (!isOpen) return;
    setValidationError('');
    setResetSent(false);
    clearError();
  }, [isOpen, mode, clearError]);

  // S27 (2026-06-04) — Sync internal `mode` with the latest
  // `defaultMode` prop each time the modal opens. Pre-S27 the
  // internal `mode` was only seeded from `defaultMode` on FIRST
  // mount via useState(defaultMode). The landing page keeps the
  // modal mounted across opens (gates with `isOpen`, not
  // mount/unmount), so a user who first triggered "Sign up" then
  // clicked "Sign in" still saw the Sign Up form — the prop
  // change never propagated. Re-syncing on every isOpen rising
  // edge fixes that. Skip the sync when the auth state is in
  // recovery mode, since the separate updatePassword effect
  // takes priority there.
  useEffect(() => {
    if (!isOpen) return;
    if (inPasswordRecovery) return;
    setMode(defaultMode);
  }, [isOpen, defaultMode, inPasswordRecovery]);

  if (!isOpen) return null;

  const validatePassword = () => {
    // S54 (2026-06-06) — bumped 6 → 8 chars to match the server-side
    // signup-with-invite requirement (line 111). Pre-S54 mismatch:
    // client accepted 6 chars, server rejected <8 with 400 weak_password,
    // user saw an opaque server error after submission. Real user case:
    // invitee typed exactly 6 chars (the documented minimum), form
    // submitted, server rejected, modal showed the error briefly but
    // the user thought signup just "didn't work."
    if (mode === 'signup' && password.length < 8) {
      setValidationError('Password must be at least 8 characters long');
      return false;
    }
    setValidationError('');
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      if (mode === 'reset') {
        const ok = await resetPassword(email);
        if (ok) setResetSent(true);
      } else if (mode === 'signin') {
        await signIn(email, password);
      } else if (mode === 'updatePassword') {
        if (password.length < 8) { setValidationError('Password must be at least 8 characters long'); return; }
        const ok = await updatePassword(password);
        if (ok) {
          setPasswordUpdated(true);
          // Brief success state then close — user is now signed in with the new password.
          setTimeout(() => {
            setPassword('');
            setValidationError('');
            setMode('signin');
            onClose();
          }, 1800);
        }
      } else {
        if (!validatePassword()) return;
        // S52 (2026-06-06) — accept the URL `?invite=...` email_invitations
        // token as an alternative gate. Pre-S52 the validation forced the
        // user to type an invitation_code into the form even though the
        // URL already carried a valid email-invitation token. Now: if
        // either the typed code OR the URL token is present, validation
        // passes and the server-side gate (signup-with-invite) handles
        // the actual verification.
        const urlInviteToken = (() => {
          try { return new URLSearchParams(window.location.search).get('invite')?.trim() ?? ''; }
          catch { return ''; }
        })();
        if (INVITE_REQUIRED && !inviteCode.trim() && !urlInviteToken) {
          setValidationError('Tradeatory is in closed beta. An invite code or invitation link is required.');
          return;
        }
        // S51 (2026-06-06) — Removed the unconditional `setCooldownTime(45)`
        // that used to fire here after every signUp call.
        //
        // Pre-S51 path: any signup attempt → 45s cooldown, regardless of
        // success / failure / failure reason. Real user case: invitee got
        // a fresh code, mistyped it once, fixed it, hit submit — was
        // blocked for 45s by the cooldown even though Supabase had not
        // rate-limited them at all. The button showed "Wait 28s" while
        // the user thought they were just slow on the form. On success
        // the cooldown was invisible (the useEffect at line 50 closes
        // the modal once `user` becomes truthy) but the cooldown state
        // STILL fired — wasted lifecycle for no benefit. The cooldown
        // now only fires from the catch branch below when Supabase
        // explicitly returns a rate_limit response, which is the only
        // case where the user actually NEEDS to wait.
        await signUp(email, password, inviteCode.trim() || undefined);
      }
    } catch (err) {
      // S51 — only block-and-wait when Supabase says we're rate-limited.
      // Other errors (invalid code, password too short, network glitch)
      // should allow immediate retry — those are user-correctable in <5
      // seconds and forcing a 45s wait is hostile. The clarifying hint
      // tells the user this is Supabase's anti-abuse limit, not us.
      if (err instanceof Error && err.message.includes('rate_limit')) {
        setCooldownTime(45);
        setValidationError(
          "Supabase's anti-abuse rate limit kicked in. Wait 45 seconds, then try again. " +
          'This is per-IP and clears automatically.',
        );
      }
      setPassword('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getErrorMessage = () => {
    if (validationError) return validationError;
    if (!error) return null;
    if (error.includes('invalid_credentials') || error.toLowerCase().includes('invalid login')) {
      return mode === 'signin'
        ? 'Invalid email or password. Please try again.'
        : 'Unable to create account. Please try a different email.';
    }
    if (error.includes('rate_limit')) return 'Please wait before trying again.';
    return error;
  };

  const isButtonDisabled = isSubmitting || (mode === 'signup' && cooldownTime > 0);

  const title =
    mode === 'signin'         ? 'Sign In' :
    mode === 'signup'         ? 'Create Account' :
    mode === 'updatePassword' ? 'Set New Password' :
                                'Reset Password';

  const switchMode = (next: Mode) => {
    setMode(next);
    setPassword('');
    setValidationError('');
    setResetSent(false);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-medium text-gray-800">{title}</h3>
          {/* Hide the dismiss button when recovery is in progress (must
              finish setting password) or when the parent passed
              dismissible={false} (e.g. AuthGate — no app behind the modal). */}
          {mode !== 'updatePassword' && dismissible && (
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-4">
          {getErrorMessage() && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md flex items-center text-red-700">
              <AlertTriangle className="w-5 h-5 mr-2" />
              {getErrorMessage()}
            </div>
          )}

          {mode === 'reset' && resetSent && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-start text-green-700">
              <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Password reset link sent</p>
                <p className="text-sm mt-1">Check your inbox at <strong>{email}</strong>. The link expires in 1 hour.</p>
              </div>
            </div>
          )}

          {mode === 'updatePassword' && passwordUpdated && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-md flex items-start text-green-700">
              <CheckCircle className="w-5 h-5 mr-2 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Password updated</p>
                <p className="text-sm mt-1">You're signed in with your new password.</p>
              </div>
            </div>
          )}

          <div className="space-y-4">
            {mode !== 'updatePassword' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email Address
                </label>
                <div className="relative">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="you@example.com"
                    required
                    autoComplete="email"
                  />
                  <Mail className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
              </div>
            )}

            {mode !== 'reset' && (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-sm font-medium text-gray-700">
                    {mode === 'updatePassword' ? 'New Password' : 'Password'}
                  </label>
                  {mode === 'signin' && (
                    <button
                      type="button"
                      onClick={() => switchMode('reset')}
                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      Forgot password?
                    </button>
                  )}
                </div>
                <div className="relative">
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (validationError) validatePassword();
                    }}
                    className="w-full pl-10 pr-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="••••••••"
                    required
                    minLength={mode === 'signup' || mode === 'updatePassword' ? 8 : undefined}
                    autoComplete={mode === 'signup' || mode === 'updatePassword' ? 'new-password' : 'current-password'}
                  />
                  <Lock className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
                </div>
                {(mode === 'signup' || mode === 'updatePassword') && (
                  <p className="mt-1 text-sm text-gray-500">
                    Password must be at least 8 characters long
                  </p>
                )}
              </div>
            )}

            {/* M-H — invite code field. Only shown on the signup form
                AND only when VITE_BETA_INVITE_REQUIRED is set to "true".
                The actual gate runs server-side (signup-with-invite); this
                field is a UX hint so users know what's expected. */}
            {mode === 'signup' && INVITE_REQUIRED && (() => {
              // S52 — when the URL carries an email_invitations token,
              // the code field is optional (the URL token satisfies the
              // server-side gate). Show a green hint so the recipient
              // knows they don't have to hunt for a code.
              let hasUrlToken = false;
              try {
                hasUrlToken = !!new URLSearchParams(window.location.search).get('invite');
              } catch { /* no-op */ }
              return (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Beta invite code {hasUrlToken && <span className="text-green-700 font-normal text-xs">(optional — your invitation link already covers this)</span>}
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={inviteCode}
                      onChange={(e) => setInviteCode(e.target.value)}
                      className="w-full pl-10 pr-3 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm"
                      placeholder={hasUrlToken ? 'leave blank — invitation link in the URL covers this' : 'paste from your invite email'}
                      required={!hasUrlToken}
                      autoComplete="off"
                    />
                    <Key className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    {hasUrlToken
                      ? 'Your admin invitation is in the URL. Just enter your email + password and click sign up.'
                      : <>Tradeatory is in closed beta. Don't have a code? Email <a href="mailto:hello@tradeatory.com" className="text-blue-600 hover:text-blue-700">hello@tradeatory.com</a>.</>}
                  </p>
                </div>
              );
            })()}

            <button
              type="submit"
              disabled={
                isButtonDisabled ||
                (mode === 'reset' && resetSent) ||
                (mode === 'updatePassword' && passwordUpdated)
              }
              className={`w-full rounded-md px-4 py-2 font-medium transition-colors flex items-center justify-center ${
                isButtonDisabled || (mode === 'reset' && resetSent) || (mode === 'updatePassword' && passwordUpdated)
                  ? 'bg-gray-300 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}
            >
              {isSubmitting ? (
                'Processing...'
              ) : cooldownTime > 0 ? (
                <><Timer className="w-4 h-4 mr-2" />Wait {cooldownTime}s</>
              ) : (
                mode === 'signin'         ? 'Sign In' :
                mode === 'signup'         ? 'Create Account' :
                mode === 'updatePassword' ? (passwordUpdated ? 'Updated' : 'Update Password') :
                resetSent                  ? 'Email Sent' :
                                             'Send Reset Link'
              )}
            </button>
          </div>

          <div className="mt-4 text-center text-sm text-gray-600">
            {mode === 'signin' && (
              <>Don't have an account?{' '}
                <button type="button" onClick={() => switchMode('signup')} className="text-blue-600 hover:text-blue-700 font-medium">Sign Up</button>
              </>
            )}
            {mode === 'signup' && (
              <>Already have an account?{' '}
                <button type="button" onClick={() => switchMode('signin')} className="text-blue-600 hover:text-blue-700 font-medium">Sign In</button>
              </>
            )}
            {mode === 'reset' && (
              <>Remembered it?{' '}
                <button type="button" onClick={() => switchMode('signin')} className="text-blue-600 hover:text-blue-700 font-medium">Back to Sign In</button>
              </>
            )}
            {mode === 'updatePassword' && !passwordUpdated && (
              <>Choose a new password to complete recovery. You'll be signed in automatically.</>
            )}
          </div>
        </form>
      </div>
    </div>
  );
};

export default AuthModal;
